import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ScrapedContent {
  title: string;
  description: string;
  headings: string[];
  paragraphs: string[];
  links: string[];
  imageUrls: string[];
  rawText: string;
  url: string;
  success: boolean;
  error?: string;
  // Enhanced image fields from scraper
  bestImage?: string;
  ogImage?: string;
  logoUrl?: string;
  structuredLogo?: string;
}

export function useUrlScraper() {
  const [isScraping, setIsScraping] = useState(false);

  const scrapeUrls = async (urls: string[]): Promise<Map<string, ScrapedContent>> => {
    if (urls.length === 0) return new Map();
    
    setIsScraping(true);
    const resultsMap = new Map<string, ScrapedContent>();
    
    try {
      // Filter valid URLs
      const validUrls = urls.filter(url => {
        try {
          new URL(url);
          return true;
        } catch {
          return false;
        }
      });
      
      if (validUrls.length === 0) {
        return resultsMap;
      }
      
      console.log(`Scraping ${validUrls.length} URLs...`);
      
      const { data, error } = await supabase.functions.invoke('scrape-url', {
        body: { urls: validUrls }
      });
      
      if (error) {
        console.error('Scrape error:', error);
        toast.error('Failed to scrape some URLs');
        return resultsMap;
      }
      
      const results = data?.results as ScrapedContent[] || [];
      
      for (const result of results) {
        resultsMap.set(result.url, result);
      }
      
      const successful = results.filter(r => r.success).length;
      if (successful > 0) {
        toast.success(`Scraped ${successful}/${validUrls.length} URLs`);
      }
      
      return resultsMap;
    } catch (error) {
      console.error('Scraping error:', error);
      toast.error('URL scraping failed');
      return resultsMap;
    } finally {
      setIsScraping(false);
    }
  };

  return { scrapeUrls, isScraping };
}
